// Shared presentational primitives.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Access } from '../types';

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row row-tight">
      <span className="spinner" />
      {label ? <span className="muted small">{label}</span> : null}
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty">
      <Spinner label={label} />
    </div>
  );
}

export function Empty({ icon = '◌', title, children }: { icon?: string; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div style={{ fontSize: 30, marginBottom: 8 }}>{icon}</div>
      <h3>{title}</h3>
      {children ? <div className="small">{children}</div> : null}
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="alert alert-error">{message}</div>;
}

export function AccessBadge({ access }: { access: Access }) {
  const label = access.charAt(0).toUpperCase() + access.slice(1);
  const className =
    access === 'public' ? 'badge badge-public'
    : access === 'open' ? 'badge badge-open'
    : 'badge';
  return <span className={className}>{label}</span>;
}

export function Modal({
  title,
  onClose,
  children,
  wide = false,
  xl = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  /** Wider still than `wide` — for content that wants real screen real
   *  estate, like a scrolling log viewer. */
  xl?: boolean;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className={`modal ${xl ? 'modal-xl' : wide ? 'modal-wide' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="card-title">
          <h2>{title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Copy-to-clipboard box for snippets, URLs and keys. Actions float in the
 * top-right corner rather than sitting inline after the text — inline
 * pushed them past the end of long or multi-line content (a real SKILL.md,
 * a long curl command), off the visible edge of the box until scrolled to.
 * Floating keeps them reachable regardless of content length.
 */
export function CopyBox({
  value,
  masked = false,
  downloadHref,
  downloadName,
}: {
  value: string;
  masked?: boolean;
  /** When set, adds a Download action pointing at this URL instead of copying `value`. */
  downloadHref?: string;
  downloadName?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(!masked);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard access can be blocked; selecting the text still works.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const shown = revealed ? value : `${value.slice(0, 6)}${'•'.repeat(Math.max(value.length - 6, 8))}`;

  return (
    <div className="copy-box">
      <pre className="copy-box-content">
        <code>{shown}</code>
      </pre>
      <div className="copy-box-actions">
        {masked ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRevealed((v) => !v)}>
            {revealed ? 'Hide' : 'Show'}
          </button>
        ) : null}
        {downloadHref ? (
          <a className="btn btn-sm" href={downloadHref} download={downloadName}>
            Download
          </a>
        ) : null}
        <button type="button" className="btn btn-sm" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/**
 * Generic log viewer modal — image builds today (Admin's Image Builds tab,
 * Developers' Registry tab), potentially docker apps or sessions later.
 * Polls `fetchLogs` every few seconds while `status` is non-terminal, and
 * stops once it settles (a caller passes `undefined` for content that's
 * never "in progress" at all, e.g. a one-shot log dump). Copy/Download work
 * entirely client-side off the already-fetched text — no extra endpoint or
 * round-trip needed just to save it to a file.
 */
export function LogsModal({
  title,
  status,
  error,
  fetchLogs,
  downloadName = 'build.log',
  onClose,
}: {
  title: string;
  status?: 'pending' | 'running' | 'succeeded' | 'failed';
  error?: string | null;
  fetchLogs: () => Promise<string>;
  downloadName?: string;
  onClose: () => void;
}) {
  const [logs, setLogs] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<unknown>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    fetchLogs().then(setLogs).catch(setFetchError);
  }, [fetchLogs]);

  useEffect(() => {
    load();
    if (status !== 'pending' && status !== 'running') return;
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
  }, [load, status]);

  const downloadUrl = useMemo(() => {
    if (!logs) return undefined;
    return URL.createObjectURL(new Blob([logs], { type: 'text/plain' }));
  }, [logs]);
  useEffect(() => () => { if (downloadUrl) URL.revokeObjectURL(downloadUrl); }, [downloadUrl]);

  const copy = async () => {
    if (!logs) return;
    try {
      await navigator.clipboard.writeText(logs);
    } catch {
      // Clipboard access can be blocked; selecting the text still works.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Modal title={title} onClose={onClose} xl>
      <ErrorMessage error={fetchError} />
      {error ? <div className="alert alert-error">{error}</div> : null}
      {logs === null ? (
        <Loading />
      ) : (
        <>
          <div className="row" style={{ marginBottom: '0.75rem' }}>
            <button type="button" className="btn btn-sm" onClick={copy} disabled={!logs}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a className="btn btn-sm" href={downloadUrl} download={downloadName}>
              Download
            </a>
          </div>
          <pre className="md" style={{ maxHeight: '65vh', overflow: 'auto' }}>
            <code>{logs || 'No logs yet.'}</code>
          </pre>
        </>
      )}
    </Modal>
  );
}

/**
 * A joined set of mutually exclusive options.
 *
 * Distinct from `Tabs`: tabs read as sections of a page, this swaps what a page
 * is showing. Used for both the scope and the kind filter on the project list,
 * which is why it is a component rather than markup repeated twice.
 */
export function Segmented<T extends string>({
  options,
  active,
  onChange,
  label,
  disabled = false,
}: {
  options: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={active === option.id ? 'active' : ''}
          onClick={() => onChange(option.id)}
          aria-pressed={active === option.id}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; hidden?: boolean }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs">
      {tabs
        .filter((tab) => !tab.hidden)
        .map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab ${active === tab.id ? 'active' : ''}`}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
    </div>
  );
}

export interface DailyChartPoint {
  day: string;
  value: number;
  tooltip?: string;
}

/** A handful of evenly-spaced tick indices — every day when the range is
 *  short enough to fit, otherwise a sparse set (first, last, and evenly
 *  spaced in between) so labels never collide. */
function tickIndices(count: number, max = 5): Set<number> {
  if (count <= max) return new Set(Array.from({ length: count }, (_, i) => i));
  const ticks = new Set<number>();
  for (let i = 0; i < max; i++) ticks.add(Math.round((i * (count - 1)) / (max - 1)));
  return ticks;
}

function formatTick(day: string, weekday: boolean): string {
  const parsed = new Date(day);
  if (Number.isNaN(parsed.getTime())) return day;
  return weekday
    ? parsed.toLocaleDateString(undefined, { weekday: 'short' })
    : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A single-series day-by-day bar chart with a sparse axis of date labels.
 *  `tone` only tells two charts on the same page apart (e.g. activity vs.
 *  views) — it is decorative, not a legend, so it stays unlabeled here. */
export function DailyBarChart({ data, tone = 'accent' }: { data: DailyChartPoint[]; tone?: 'accent' | 'indigo' }) {
  const peak = Math.max(1, ...data.map((d) => d.value));
  const weekday = data.length <= 7;
  const labeled = tickIndices(data.length);
  return (
    <div className={`daily-chart daily-chart-${tone}`}>
      <div className="bar-chart">
        {data.map((point) => (
          <div
            key={point.day}
            className="bar"
            style={{ height: `${(point.value / peak) * 100}%` }}
            title={point.tooltip ?? `${formatTick(point.day, false)}: ${point.value}`}
          />
        ))}
      </div>
      <div className="bar-chart-labels">
        {data.map((point, i) => (
          <span key={point.day} className="bar-chart-label">
            {labeled.has(i) ? formatTick(point.day, weekday) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export function StatTile({
  label,
  value,
  hint,
  action,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="stat-tile">
      <div className="row row-tight" style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div className="stat-value">{value}</div>
        {action}
      </div>
      <div className="stat-label">{label}</div>
      {hint ? <div className="faint small" style={{ marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}

/**
 * A labeled utilization bar — used by the Capacity tab for cpu/memory/pod
 * headroom. Color is a status signal (good/warning/critical), never the only
 * way the value is conveyed: the percentage is always printed as text too.
 */
export function Meter({
  label,
  value,
  detail,
  thresholds = { warn: 70, critical: 90 },
}: {
  label: string;
  value: number;
  detail?: string;
  thresholds?: { warn: number; critical: number };
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const tone = clamped >= thresholds.critical ? 'danger' : clamped >= thresholds.warn ? 'warning' : 'good';
  return (
    <div className="meter">
      <div className="meter-head">
        <span className="small">{label}</span>
        <span className={`meter-value meter-value-${tone}`}>{clamped.toFixed(0)}%</span>
      </div>
      <div className="meter-track">
        <div className={`meter-fill meter-fill-${tone}`} style={{ width: `${clamped}%` }} />
      </div>
      {detail ? <div className="faint small" style={{ marginTop: 4 }}>{detail}</div> : null}
    </div>
  );
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatCredits(value: number): string {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

/** Confirm dialog that returns a promise-like flow through state. */
export function useConfirm() {
  const [pending, setPending] = useState<{ message: string; resolve: (ok: boolean) => void } | null>(null);

  const confirm = (message: string) =>
    new Promise<boolean>((resolve) => {
      setPending({ message, resolve });
    });

  const element = pending ? (
    <Modal
      title="Are you sure?"
      onClose={() => {
        pending.resolve(false);
        setPending(null);
      }}
    >
      <p>{pending.message}</p>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button
          type="button"
          className="btn"
          onClick={() => {
            pending.resolve(false);
            setPending(null);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => {
            pending.resolve(true);
            setPending(null);
          }}
        >
          Confirm
        </button>
      </div>
    </Modal>
  ) : null;

  return { confirm, element };
}
